import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { FaExpand, FaFilePdf } from "react-icons/fa6";
import { Document, Page } from "react-pdf";
import { useSelector } from "react-redux";
import PdfViewerModal, { pdfFileNameFrom, Spinner, usePdfBlobUrl } from "../../../components/PdfViewerModal";

// Matches the footprint the old iframe preview occupied (h-64, ~300px wide).
const PREVIEW_W = 320;
const PREVIEW_MAX_H = 256;

const PdfMessage = ({ message }) => {
  const { theme } = useSelector((state) => state.theme);
  const isDark = theme === "dark";

  const url = message?.media?.file;
  const name = message?.media?.name ?? pdfFileNameFrom(url);

  const [isOpen, setIsOpen] = useState(false);
  // A thread can hold many PDFs; only fetch and rasterise the ones on screen.
  const [inView, setInView] = useState(false);
  const [numPages, setNumPages] = useState(0);
  const [renderError, setRenderError] = useState(null);
  const [pageWidth, setPageWidth] = useState(0);
  const containerRef = useRef(null);

  const { blobUrl, error, retry } = usePdfBlobUrl(inView ? url : null);
  const shownError = error || renderError;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Pages draw to canvas at a fixed pixel width, so track the bubble's width.
  const measure = useCallback(() => {
    const el = containerRef.current;
    if (el) setPageWidth(Math.max(0, el.clientWidth));
  }, []);

  useLayoutEffect(() => {
    measure();
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  const caption = message?.message ? (
    <div className="mt-2 text-sm">{message.message}</div>
  ) : null;

  if (!url) {
    return (
      <div>
        <p className="text-sm italic opacity-70">Document unavailable</p>
        {caption}
      </div>
    );
  }

  return (
    <>
      <div ref={containerRef} style={{ width: PREVIEW_W, maxWidth: "100%" }}>
        {shownError ? (
          <div
            className={`rounded-lg border p-3 text-center ${
              isDark ? "border-gray-700 bg-gray-800/60" : "border-gray-200 bg-gray-50"
            }`}
          >
            <FaFilePdf className="mx-auto mb-2 text-red-500" size={22} />
            <p className={`text-xs ${isDark ? "text-gray-300" : "text-gray-600"}`}>{shownError}</p>
            <div className="flex items-center justify-center gap-2 mt-2">
              <button
                type="button"
                onClick={() => {
                  setRenderError(null);
                  retry();
                }}
                className="px-3 py-1 text-xs text-white bg-purple-600 rounded hover:bg-purple-700"
              >
                Try again
              </button>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1 text-xs text-blue-500 underline"
              >
                Open in new tab
              </a>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setIsOpen(true)}
            title={name}
            aria-label={`Open ${name}`}
            className={`block w-full overflow-hidden text-left border rounded-lg group/pdf transition-shadow hover:shadow-md ${
              isDark ? "border-gray-700 bg-gray-900" : "border-gray-200 bg-gray-100"
            }`}
          >
            <div className="relative overflow-hidden" style={{ maxHeight: PREVIEW_MAX_H }}>
              {blobUrl && pageWidth > 0 ? (
                <Document
                  file={blobUrl}
                  onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                  onLoadError={(err) => setRenderError(err?.message || "Could not render this PDF.")}
                  loading={<Spinner label="Loading preview…" />}
                  error={null}
                >
                  <Page
                    pageNumber={1}
                    width={pageWidth}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    loading={null}
                  />
                </Document>
              ) : (
                <Spinner label="Loading preview…" />
              )}

              {/* Fade the clipped page edge. Keyed to the page, which is white in
                  both themes, not to the bubble behind it. */}
              <div className="absolute inset-x-0 bottom-0 h-10 pointer-events-none bg-gradient-to-t from-white to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center transition-opacity opacity-0 pointer-events-none bg-black/30 group-hover/pdf:opacity-100">
                <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-800 bg-white rounded-full shadow">
                  <FaExpand size={11} /> Open
                </span>
              </div>
            </div>

            <div
              className={`flex items-center gap-2 px-3 py-2 border-t ${
                isDark ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
              }`}
            >
              <FaFilePdf className="text-red-500 shrink-0" size={16} />
              <span
                className={`flex-1 min-w-0 text-xs font-medium truncate ${
                  isDark ? "text-gray-300" : "text-gray-700"
                }`}
              >
                {name}
              </span>
              {numPages > 0 && (
                <span className={`text-[10px] shrink-0 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                  {numPages} {numPages === 1 ? "page" : "pages"}
                </span>
              )}
            </div>
          </button>
        )}

        {caption}
      </div>

      {isOpen && (
        <PdfViewerModal
          url={blobUrl ?? url}
          title={name}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

export default PdfMessage;
